import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as InstitutionItemTemplateActions from "../../../actions/institutionItemTemplate";
import * as ItemComponentTypeActions from "../../../actions/itemComponentType";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import { TextAreaFormInput } from "../../../components/form";
import { CheckboxFormInput, NumberFormInput, SelectFormInput, TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { CollectionPage } from "../../../containers/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { InstitutionItemTemplateBreadcrumbs, InstitutionItemTemplateCreateBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { SettingsInstitutionsSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { InstitutionLookupsLayout } from "../../index";

import "./InstitutionItemTemplatePage.scss";

interface InstitutionItemTemplatePageProps {
    form: Dtos.InstitutionItemTemplate & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    institutionItemTemplate: Dtos.InstitutionItemTemplate;
    collection: Dtos.Collection;
    lookups: Dtos.Lookup[];
    loadingInstitutionItemTemplate: boolean;
    loadInstitutionItemTemplateFailure: boolean;
    loadInstitutionItemTemplateErrorCode: string;
    savingInstitutionItemTemplate: boolean;
    saveInstitutionItemTemplateSuccess: boolean;
    saveInstitutionItemTemplateFailure: boolean;
    creatingInstitutionItemTemplate: boolean;
    createInstitutionItemTemplateSuccess: boolean;
    validatingInstitutionItemTemplate: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    itemComponentTypes: Dtos.ItemComponentType[];
    loadingItemComponentTypes: boolean;
    loadItemComponentTypesSuccess: boolean;
    loadItemComponentTypesFailure: boolean;
    loadItemComponentTypesErrorCode: string;
    clearInstitutionItemTemplate: InstitutionItemTemplateActions.IInstitutionItemTemplateClearActionCreator;
    loadInstitutionItemTemplate: InstitutionItemTemplateActions.IInstitutionItemTemplateLoadByIdActionCreator;
    saveInstitutionItemTemplate: InstitutionItemTemplateActions.IInstitutionItemTemplateSaveActionCreator;
    createInstitutionItemTemplate: InstitutionItemTemplateActions.IInstitutionItemTemplateCreateActionCreator;
    validateInstitutionItemTemplate: InstitutionItemTemplateActions.IInstitutionItemTemplateValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeClearActionCreator;
    loadItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeLoadActionCreator;
}

const reduxFormName: string = "reduxForms.institutionItemTemplate";

const InstitutionItemTemplatePage: FC<InstitutionItemTemplatePageProps> = ({
    form,
    formState,
    formProperties,
    institutionItemTemplate,
    collection,
    lookups,
    loadingInstitutionItemTemplate,
    loadInstitutionItemTemplateFailure,
    loadInstitutionItemTemplateErrorCode,
    savingInstitutionItemTemplate,
    saveInstitutionItemTemplateSuccess,
    saveInstitutionItemTemplateFailure,
    creatingInstitutionItemTemplate,
    createInstitutionItemTemplateSuccess,
    validatingInstitutionItemTemplate,
    validationFailures,
    permissions,
    itemComponentTypes,
    loadingItemComponentTypes,
    // loadItemComponentTypesSuccess,
    loadItemComponentTypesFailure,
    loadItemComponentTypesErrorCode,
    clearInstitutionItemTemplate,
    loadInstitutionItemTemplate,
    saveInstitutionItemTemplate,
    createInstitutionItemTemplate,
    validateInstitutionItemTemplate,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearItemComponentTypes,
    loadItemComponentTypes
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{institutionId: string;}>();
    const institutionIdNumber = parseInt(institutionId, 10);
    const { institutionItemTemplateId } = useParams<{institutionItemTemplateId: string;}>();
    const institutionItemTemplateIdNumber = parseInt(institutionItemTemplateId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.InstitutionItemTemplate = {} as Dtos.InstitutionItemTemplate;

        if (!isNaN(institutionIdNumber)) {
            extraData.institutionId = institutionIdNumber;
        }

        loadForm(reduxFormName, Object.assign({ ...institutionItemTemplate }, extraData));
    };


    // Load institution item template
    useEffect(() => {
        if (
            institutionItemTemplateIdNumber &&
            !institutionItemTemplate &&
            !loadingInstitutionItemTemplate
        ) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(
                InstitutionItemTemplateBreadcrumbs(
                    institutionItemTemplateIdNumber,
                    isNaN(institutionIdNumber) ? undefined : institutionIdNumber
                )
            );
            loadInstitutionItemTemplate(institutionItemTemplateIdNumber);
        } else if (
            !institutionItemTemplateIdNumber &&
            !creatingInstitutionItemTemplate &&
            !createInstitutionItemTemplateSuccess
        ) {
            createInstitutionItemTemplate(isNaN(institutionIdNumber) ? undefined : institutionIdNumber);
            setBreadcrumbs(
                InstitutionItemTemplateCreateBreadcrumbs(isNaN(institutionIdNumber) ? undefined : institutionIdNumber)
            );
            setDrawerItems([]);
        } else if (institutionItemTemplate && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }

        if (!itemComponentTypes && !loadingItemComponentTypes) {
            loadItemComponentTypes();
        }
    }, [
        institutionItemTemplateIdNumber,
        institutionItemTemplate,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        InstitutionItemTemplateBreadcrumbs,
        loadInstitutionItemTemplate,
        createInstitutionItemTemplate,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingInstitutionItemTemplate,
        creatingInstitutionItemTemplate,
        createInstitutionItemTemplateSuccess,
        InstitutionItemTemplateCreateBreadcrumbs,
        itemComponentTypes,
        loadingItemComponentTypes,
        loadItemComponentTypes,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionItemTemplateFailure) {
            navigate(`/error/${loadInstitutionItemTemplateErrorCode}`);
        }
        if (loadItemComponentTypesFailure) {
            navigate(`/error/${loadItemComponentTypesErrorCode}`);
        }
    }, [
        loadInstitutionItemTemplateFailure,
        loadInstitutionItemTemplateErrorCode,
        loadItemComponentTypesFailure,
        loadItemComponentTypesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionItemTemplateIdNumber && institutionItemTemplate?.name) {
            setBreadcrumbs(
                InstitutionItemTemplateBreadcrumbs(
                    institutionItemTemplateIdNumber,
                    isNaN(institutionIdNumber) ? undefined : institutionIdNumber,
                    institutionItemTemplate?.name
                )
            );
        }
    }, [
        institutionItemTemplateIdNumber,
        institutionItemTemplate,
        setBreadcrumbs,
        InstitutionItemTemplateBreadcrumbs,
    ]);

    // Reload institution item template after saving
    useEffect(() => {
        if (!savingInstitutionItemTemplate && saveInstitutionItemTemplateSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution item template saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = institutionIdNumber
                    ? `/settings/institution/itemtemplates/${institutionIdNumber}`
                    : "/settings/institution/itemtemplates";
                navigate(url);
            } else if (!institutionItemTemplateIdNumber) {
                navigate(
                    `/settings/institution/itemtemplates/${institutionItemTemplate?.institutionId}/template/${institutionItemTemplate?.id}`
                );
                setInitalised(false);
                clearInstitutionItemTemplate();
            }
        }
    }, [
        savingInstitutionItemTemplate,
        saveInstitutionItemTemplateSuccess,
        institutionItemTemplateIdNumber,
        navigate,
        clearInstitutionItemTemplate,
        saveAndReturn,
        institutionItemTemplate,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingInstitutionItemTemplate && saveInstitutionItemTemplateFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingInstitutionItemTemplate, saveInstitutionItemTemplateFailure]);

    // Cleanup effect to clear the institution item template on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionItemTemplate();
            clearItemComponentTypes();
            resetForm(reduxFormName);
        };
    }, [
        clearInstitutionItemTemplate,
        clearItemComponentTypes,
        resetForm,
        reduxFormName,
    ]);

    const onCancel = () => {
        let url: string = institutionIdNumber
            ? `/settings/institution/itemtemplates/${institutionIdNumber}`
            : "/settings/institution/itemtemplates";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.InstitutionItemTemplate) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.InstitutionItemTemplate = {} as Dtos.InstitutionItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!data?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            await saveInstitutionItemTemplate(
                Object.assign({ ...data }, changes),
                Object.assign({ ...collection }, collectionChanges)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.InstitutionItemTemplate = {} as Dtos.InstitutionItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!form?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            if (form && hasBeenSubmitted && !validatingInstitutionItemTemplate) {
                await validateInstitutionItemTemplate(
                    Object.assign({ ...form }, changes),
                    Object.assign({ ...collection }, collectionChanges)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderCollection = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.InstitutionItemUpdate,
                Dtos.Permission.InstitutionItemCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        if (form && !loadingInstitutionItemTemplate && itemComponentTypes) {
            return (
                <CollectionPage
                    qaItemComponents={false}
                    itemComponentTypeId={
                        itemComponentTypes?.find((x) => x.value === "Institution")?.id
                    }
                    showSections={false}
                    collectionId={form.collectionId || 0}
                    readonly={formDisabled}
                />
            );
        }
    }, [
        form,
        permissions,
        savingInstitutionItemTemplate,
        formState,
        loadingInstitutionItemTemplate,
        itemComponentTypes,
    ]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.InstitutionItemUpdate,
                Dtos.Permission.InstitutionItemCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            details: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div className="card card-shadow">
                        <div className="form-row pt-4 pb-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "institutionItemTemplate",
                                    {
                                        name: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "institutionItemTemplate",
                                    {
                                        description: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextAreaFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                rows: 3,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "institutionItemTemplate",
                                    {
                                        institutionItemTemplateCategoryId: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: SelectFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                options:
                                                    generateOptionsFromLookup(
                                                        findLookup(
                                                            "InstitutionItemTemplate",
                                                            "InstitutionItemTemplateCategoryId",
                                                            lookups
                                                        ),
                                                        form.institutionItemTemplateCategoryId
                                                    ),
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "institutionItemTemplate",
                                    {
                                        rank: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: NumberFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pt-sm-0 pt-md-2 pt-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "institutionItemTemplate",
                                    {
                                        institutionId: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: SelectFormInput,
                                            inputDisabled: formDisabled || !isNaN(institutionIdNumber),
                                            inputProps: {
                                                options:
                                                    generateOptionsFromLookup(
                                                        findLookup(
                                                            "InstitutionItemTemplate",
                                                            "InstitutionId",
                                                            lookups
                                                        ),
                                                        form.institutionId
                                                    ),
                                                clearable: true,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "institutionItemTemplate",
                                    {
                                        allowIntraVersions: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow intra versions",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                            changeAction: (_model, value, _form, _parentModel) => {
                                                changeForm(`${reduxFormName}.allowIntraVersions`, value);
                                                if (!value) {
                                                    changeForm(`${reduxFormName}.initialVersion`, null);
                                                }
                                            },
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "institutionItemTemplate",
                                    {
                                        initialVersion: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled || !form.allowIntraVersions,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row pt-2 pb-4 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "institutionItemTemplate",
                                    {
                                        allowVersionChanges: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow version label changes",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "institutionItemTemplate",
                                    {
                                        active: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Active",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pt-sm-0 pt-md-2 pt-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                    </div>
                ),
            },
            components: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Components</h1>
                        {renderCollection}
                    </>
                ),
            },
        };

        return (
            <div>
                <h1>Institution Item Template</h1>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "institutionItemTemplate",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingInstitutionItemTemplate,
        formState,
        lookups,
        generateOptionsFromLookup,
        findLookup,
        renderCollection,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        institutionIdNumber,
    ]);

    return (
        <InstitutionLookupsLayout
            loading={
                loadingInstitutionItemTemplate ||
                creatingInstitutionItemTemplate
            }
            permissions={permissions}
            permission={Dtos.Permission.InstitutionItemView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<InstitutionItemTemplatePageProps> => {
    let institutionItemTemplate: Dtos.InstitutionItemTemplate | undefined = 
        !(state.institutionItemTemplate.formData instanceof Array)
            ? state.institutionItemTemplate.formData
            : undefined;
    let collection: Dtos.Collection | undefined = 
        !(state.reduxForms.collection instanceof Array)
            ? state.reduxForms.collection
            : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];
    let itemComponentTypes = state.itemComponentTypes.formData instanceof Array
        ? state.itemComponentTypes.formData
        : undefined;

    return {
        institutionItemTemplate: institutionItemTemplate!,
        collection: collection!,
        loadingInstitutionItemTemplate:
            state.institutionItemTemplate.loadState &&
            state.institutionItemTemplate.loadState.status == RequestState.Pending,
        loadInstitutionItemTemplateFailure:
            state.institutionItemTemplate.loadState &&
            state.institutionItemTemplate.loadState.status == RequestState.Failure,
        loadInstitutionItemTemplateErrorCode: state.institutionItemTemplate.loadState.errorCode,
        savingInstitutionItemTemplate:
            state.institutionItemTemplate.saveState &&
            state.institutionItemTemplate.saveState.status == RequestState.Pending,
        saveInstitutionItemTemplateSuccess:
            state.institutionItemTemplate.saveState &&
            state.institutionItemTemplate.saveState.status == RequestState.Success,
        saveInstitutionItemTemplateFailure:
            state.institutionItemTemplate.saveState &&
            state.institutionItemTemplate.saveState.status == RequestState.Failure,
        creatingInstitutionItemTemplate:
            state.institutionItemTemplate.createState &&
            state.institutionItemTemplate.createState.status == RequestState.Pending,
        createInstitutionItemTemplateSuccess:
            state.institutionItemTemplate.createState &&
            state.institutionItemTemplate.createState.status == RequestState.Success,
        validatingInstitutionItemTemplate:
            state.institutionItemTemplate.validateState &&
            state.institutionItemTemplate.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.institutionItemTemplate.lookups,
        form: state.reduxForms.institutionItemTemplate,
        formState: state.institutionItemTemplate.formState,
        formProperties: state.institutionItemTemplate.formProperties,
        validationFailures: institutionItemTemplate
            ? state.institutionItemTemplate.validationFailures
            : undefined,
        itemComponentTypes: itemComponentTypes,
        loadingItemComponentTypes:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Pending,
        loadItemComponentTypesSuccess:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Success,
        loadItemComponentTypesFailure:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Failure,
        loadItemComponentTypesErrorCode: state.itemComponentTypes.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionItemTemplatePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadInstitutionItemTemplate: bindActionCreators(
            InstitutionItemTemplateActions.LoadInstitutionItemTemplateById,
            dispatch
        ),
        clearInstitutionItemTemplate: bindActionCreators(
            InstitutionItemTemplateActions.Clear,
            dispatch
        ),
        saveInstitutionItemTemplate: bindActionCreators(
            InstitutionItemTemplateActions.SaveInstitutionItemTemplate,
            dispatch
        ),
        createInstitutionItemTemplate: bindActionCreators(
            InstitutionItemTemplateActions.CreateInstitutionItemTemplate,
            dispatch
        ),
        validateInstitutionItemTemplate: bindActionCreators(
            InstitutionItemTemplateActions.ValidateInstitutionItemTemplate,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadItemComponentTypes: bindActionCreators(ItemComponentTypeActions.LoadItemComponentType, dispatch),
        clearItemComponentTypes: bindActionCreators(ItemComponentTypeActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InstitutionItemTemplatePage);
